import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Star, StarOff, Pencil, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { SupplierProductModal } from './supplier-product-modal';

interface SupplierProductsCardProps {
  supplierId: string;
  supplierName: string;
}

export function SupplierProductsCard({ supplierId, supplierName }: SupplierProductsCardProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const utils = trpc.useUtils();

  const { data: products, isLoading } = trpc.tradingSupplierProduct.bySupplier.useQuery(supplierId);

  const deleteMutation = trpc.tradingSupplierProduct.delete.useMutation({
    onSuccess: () => {
      utils.tradingSupplierProduct.bySupplier.invalidate(supplierId);
      utils.tradingSupplier.list.invalidate();
      showSuccessToast(tCommon('deleted'));
      setDeleteId(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const setPreferredMutation = trpc.tradingSupplierProduct.setPreferred.useMutation({
    onSuccess: () => {
      utils.tradingSupplierProduct.bySupplier.invalidate(supplierId);
      showSuccessToast(tCommon('updated'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  };

  const handleEdit = (product: any) => {
    setEditingProduct(product);
  };

  const existingProductIds = products?.map((p) => p.productId) || [];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="text-lg">{t('purchasing.supplierProducts.title')}</CardTitle>
            <CardDescription>{t('purchasing.supplierProducts.subtitle')}</CardDescription>
          </div>
          <Button size="sm" onClick={() => setAddModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('purchasing.supplierProducts.addProduct')}
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-4 text-muted-foreground">{tCommon('loading')}</div>
          ) : !products || products.length === 0 ? (
            <div className="text-center py-8">
              <Package className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-2 text-sm font-medium">{t('purchasing.supplierProducts.noProducts')}</p>
              <p className="text-sm text-muted-foreground">{t('purchasing.supplierProducts.noProductsDesc')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{product.productName}</span>
                      {product.isPreferred && (
                        <Badge variant="default" className="text-xs shrink-0">
                          <Star className="h-3 w-3 mr-1" />
                          {t('purchasing.supplierProducts.isPreferred')}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                      <span className="font-mono">{product.productCode}</span>
                      <span>{product.brandName}</span>
                      {product.supplierCode && (
                        <span>({product.supplierCode})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm">
                      <span className="font-semibold">
                        {product.unitCost.toFixed(2)} {product.currency}
                      </span>
                      <span className="text-muted-foreground">
                        MOQ: {product.minOrderQty}
                      </span>
                      {product.leadTimeDays > 0 && (
                        <span className="text-muted-foreground">
                          {product.leadTimeDays}d lead
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    {!product.isPreferred && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPreferredMutation.mutate(product.id)}
                        disabled={setPreferredMutation.isPending}
                        title={t('purchasing.supplierProducts.isPreferred')}
                      >
                        <StarOff className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(product)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(product.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <SupplierProductModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        supplierId={supplierId}
        supplierName={supplierName}
        existingProductIds={existingProductIds}
      />

      {editingProduct && (
        <SupplierProductModal
          open={!!editingProduct}
          onOpenChange={(open) => !open && setEditingProduct(null)}
          supplierId={supplierId}
          supplierName={supplierName}
          editingProduct={editingProduct}
        />
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tCommon('confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('purchasing.suppliers.deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
